import React, {
  FC,
  ReactElement,
  ReactNode,
  Ref,
  cloneElement,
  isValidElement,
  useRef,
} from "react";
import { Controller, useFormContext } from "react-hook-form";

import useI18n from "@I18n/hooks/useI18n";
import { useFeatureFlag } from "@Libs/GrowthBook/hooks/useFeatureFlag";
import { GOOGLE_MAPS_BROWSER_API_KEY, getCurrentCountry } from "@config";
import { Text } from "@UI/Components/Text/Text";
import { VStack } from "@UI/Components/Stack/Stack";
import useMediaQuery from "@UI/hooks/useMediaQuery";
import {
  AddressPlaceDetails,
  AddressQuestion,
} from "../Components/Questions/AddressQuestion/AddressQuestion";
import { missingAddressParts } from "../Components/Questions/AddressQuestion/addressCompleteness";
import { AutoFilledBadge } from "../Components/Questions/AutoFilledBadge/AutoFilledBadge";
import { DateQuestion } from "../Components/Questions/DateQuestion/DateQuestion";
import { MultiSelectQuestion } from "../Components/Questions/MultiSelectQuestion/MultiSelectQuestion";
import { NumberQuestion } from "../Components/Questions/NumberQuestion/NumberQuestion";
import { PictureUploadQuestion } from "../Components/Questions/PictureUploadQuestion/PictureUploadQuestion";
import { SingleSelectQuestion } from "../Components/Questions/SingleSelectQuestion/SingleSelectQuestion";
import { TextQuestion } from "../Components/Questions/TextQuestion/TextQuestion";

import { useTaskCreationFlow } from "../Contexts/TaskCreationFlow";
import { useDraftIdentity } from "../hooks/useDraftIdentity";
import { useSuggestedAddress } from "../hooks/useSuggestedAddress";
import {
  useForgetUploadedPhoto,
  useRecordPhotoDescription,
  useRecordUploadedPhoto,
} from "../hooks/useServiceRequestDraft";
import messages from "../messages";
import { IAnswerValue, IQuestion, IQuestionType } from "../types";
import { isTodayOrLater } from "../utils/dateBounds";
import { CONTACT_INFO_ERROR, noContactInfo } from "../utils/contactInfo";
import { resolveHints } from "../utils/resolveHints";
import { TASK_CREATION_FULL_FLAG, TASK_PHOTO_CATEGORY } from "./constants";
import {
  getConfigMessage,
  getQuestionOptionMessage,
  getQuestionTitleMessage,
} from "../utils/messageKeys";

/**
 * The seam between the config and the question components.
 *
 * Question components are presentational: they take a value, an onChange and
 * plain UI props, and know nothing about configs, categories or conditions. The
 * adapter registered here is what turns a config node into those props —
 * resolving option labels, mapping `constraints` to `maxLength`/`step`, and
 * placing the prefill badge on the right option.
 *
 * Adding a question type means adding one adapter here. A type with no entry
 * renders nothing, so an unknown type from a newer config skips its question
 * rather than breaking the screen.
 */

export interface IQuestionAdapterProps {
  /** The question's key, used as the react-hook-form field name. */
  name: string;
  /** The config node: type, options, `required`, constraints. */
  question: IQuestion;
  /** `config.category.key` — half of every i18n key an option label needs. */
  categoryKey: string;
  /**
   * The assistant's suggestion, already dropped by the caller when the user has
   * dismissed this question's prefill. Options matching it are badged for as
   * long as the answer is still exactly it.
   */
  aiValue?: IAnswerValue;
  /** Called on the first change, so the prefill never comes back. */
  onEdit?: () => void;
  /** Screens with a single question let the `<h1>` be the label. */
  showLabel?: boolean;
  /**
   * Called when an inline question is interacted with, so its trigger option
   * gets selected.
   */
  onActivate?: () => void;
  /**
   * Node to pin to the right of a given option's row. `DynamicScreen` uses it to
   * render an inline question inside its trigger option — this adapter neither
   * knows nor cares which question that is.
   */
  renderOptionTrailing?: (optionValue: string) => ReactNode;
  /**
   * The message for the inline question pinned to a given option, if it is
   * currently invalid. Kept separate from `renderOptionTrailing` because it is
   * rendered below the whole option group, not inside the option's own card.
   */
  renderOptionError?: (optionValue: string) => string | undefined;
  /**
   * Ref to the trailing control's underlying `<input>`, so a click anywhere on
   * its option's card (not just the input itself) can activate it — e.g. open
   * the date pill's native picker. Only `DateQuestionField` reads this; every
   * other adapter ignores it.
   */
  inputRef?: Ref<HTMLInputElement>;
  /**
   * Reported whenever this question has async work in flight that a submit
   * would race — `picture_upload`'s uploads/removals, today. `DynamicScreen`
   * disables Continue while any question reports busy; a type with nothing
   * async of its own simply never calls this.
   */
  onBusyChange?: (isBusy: boolean) => void;
}

export type IQuestionAdapter = FC<IQuestionAdapterProps>;

const toList = (value: IAnswerValue | undefined): string[] => {
  // `picture_upload`'s answer is also an array (attachment ids), but never
  // flows through this helper. TS can't resolve `.filter`'s overloads on a
  // union of array types (`string[] | number[]`), so the cast to `unknown[]`
  // collapses that union before the type-guard filter runs.
  if (Array.isArray(value)) {
    return (value as unknown[]).filter(
      (item): item is string => typeof item === "string"
    );
  }

  return value == null || value === "" ? [] : [String(value)];
};

/** The badge stays only while the answer is untouched — same set, same order. */
const isSameSelection = (value: string[], aiValue?: IAnswerValue) => {
  const ai = toList(aiValue);

  return (
    ai.length > 0 &&
    ai.length === value.length &&
    ai.every((option) => value.includes(option))
  );
};

/** Scalar counterpart of `isSameSelection`, for the one-value question types. */
const isSameScalar = (value: unknown, aiValue?: IAnswerValue) =>
  aiValue != null && value != null && String(value) === String(aiValue);

/** One hook call for both select adapters: the label resolver they share. */
const useOptionLabel = (categoryKey: string, questionKey: string) => {
  const { formatMessage } = useI18n();

  return (option: string) => {
    const message = getQuestionOptionMessage(categoryKey, questionKey, option);

    // The raw option string is a readable last resort — a config option with no
    // declared key should not render an empty card.
    return message ? formatMessage(message) : option;
  };
};

const useQuestionTitle = (categoryKey: string, questionKey: string) => {
  const { formatMessage } = useI18n();
  const message = getQuestionTitleMessage(categoryKey, questionKey);

  return message ? formatMessage(message) : undefined;
};

const MultiSelectQuestionField: IQuestionAdapter = ({
  name,
  question,
  categoryKey,
  aiValue,
  onEdit,
  showLabel,
}) => {
  const { formatMessage } = useI18n();
  const { control } = useFormContext();
  const optionLabel = useOptionLabel(categoryKey, question.key);
  const title = useQuestionTitle(categoryKey, question.key);
  // Design asks for one card per row on mobile and two from `lg` up.
  // `MultiSelectQuestion` takes a plain number, so the breakpoint is resolved
  // here rather than handed to Chakra as a responsive object.
  const { isDesktop } = useMediaQuery();

  return (
    <Controller
      name={name}
      control={control}
      rules={
        question.required
          ? {
              validate: (value) =>
                toList(value).length > 0 ||
                formatMessage(
                  messages["taskCreation.validation.selectAtLeastOne"]
                ),
            }
          : undefined
      }
      render={({ field, fieldState }) => {
        const value = toList(field.value);
        const isPrefilled = isSameSelection(value, aiValue);
        const prefilled = toList(aiValue);

        return (
          <MultiSelectQuestion
            label={showLabel ? title : undefined}
            options={(question.options ?? []).map((option) => ({
              value: option,
              label: optionLabel(option),
              badge:
                isPrefilled && prefilled.includes(option) ? (
                  <AutoFilledBadge />
                ) : undefined,
            }))}
            value={value}
            onChange={(next) => {
              onEdit?.();
              field.onChange(next);
            }}
            columns={isDesktop ? 2 : 1}
            isRequired={question.required}
            isInvalid={Boolean(fieldState.error)}
            errorMessage={fieldState.error?.message}
          />
        );
      }}
    />
  );
};

const SingleSelectQuestionField: IQuestionAdapter = ({
  name,
  question,
  categoryKey,
  aiValue,
  onEdit,
  showLabel,
  renderOptionTrailing,
  renderOptionError,
}) => {
  const { formatMessage } = useI18n();
  const { control } = useFormContext();
  const optionLabel = useOptionLabel(categoryKey, question.key);
  const title = useQuestionTitle(categoryKey, question.key);
  // Threaded onto whichever option's `trailing` is the date pill (via
  // `cloneElement` below) so a click anywhere on that option's card can open
  // its native picker — see `onOptionActivate` on the grid itself.
  const dateInputRef = useRef<HTMLInputElement>(null);
  // Design asks for one card per row on mobile and two from `lg` up — except
  // when the options carry hints, which the design gives a full-width card each
  // (the visibility screen). Derived from the config, not from a screen key.
  // `SingleSelectQuestion` takes a plain number, so the breakpoint is resolved
  // here rather than handed to Chakra as a responsive object.
  const { isDesktop } = useMediaQuery();
  const optionHint = (option: string) => {
    const key = question.option_hints?.[option];
    const message = key ? getConfigMessage(key) : undefined;

    return message ? formatMessage(message) : undefined;
  };
  const hasHints = Boolean(question.option_hints);

  return (
    <Controller
      name={name}
      control={control}
      rules={
        question.required
          ? {
              validate: (value) =>
                Boolean(value) ||
                formatMessage(messages["taskCreation.validation.required"]),
            }
          : undefined
      }
      render={({ field, fieldState }) => {
        const value = field.value ? String(field.value) : undefined;

        return (
          <SingleSelectQuestion
            name={name}
            label={showLabel ? title : undefined}
            options={(question.options ?? []).map((option) => {
              const trailing = renderOptionTrailing?.(option);

              return {
                value: option,
                label: optionLabel(option),
                badge:
                  value && value === aiValue && option === value ? (
                    <AutoFilledBadge />
                  ) : undefined,
                trailing:
                  trailing && isValidElement(trailing)
                    ? cloneElement(
                        trailing as ReactElement<{
                          inputRef?: Ref<HTMLInputElement>;
                        }>,
                        { inputRef: dateInputRef }
                      )
                    : trailing,
                trailingError: renderOptionError?.(option),
                hint: optionHint(option),
              };
            })}
            value={value}
            onChange={(next) => {
              onEdit?.();
              field.onChange(next);
            }}
            columns={isDesktop && !hasHints ? 2 : 1}
            isRequired={question.required}
            isInvalid={Boolean(fieldState.error)}
            errorMessage={fieldState.error?.message}
            onOptionActivate={() => {
              // showPicker throws NotAllowedError outside a user gesture. A
              // card-wide click handler is more exposed to that than the
              // input's own onClick (e.g. programmatic clicks), so guard here.
              try {
                dateInputRef.current?.showPicker?.();
              } catch {
                // Not a user gesture — nothing to do.
              }
            }}
          />
        );
      }}
    />
  );
};

/**
 * `integer` and `float`, both drawn as the stepper card from the design.
 *
 * The label sits on the card itself rather than being replaced by the `<h1>`:
 * every number screen in the config carries several questions and its own
 * screen title, so `showLabel` is not consulted here.
 *
 * TODO(RT-4321): `float` is rounded to whole numbers — `precision` is 0 for
 * both types, matching the design's whole-number `m²` mocks. Give it decimals
 * once a config or design asks for them.
 */
const NumberQuestionField: IQuestionAdapter = ({
  name,
  question,
  categoryKey,
  aiValue,
  onEdit,
}) => {
  const { formatMessage } = useI18n();
  const { control } = useFormContext();
  const title = useQuestionTitle(categoryKey, question.key);
  const min = question.constraints?.minimum;

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        validate: (value) => {
          if (value == null || value === "")
            return (
              !question.required ||
              formatMessage(messages["taskCreation.validation.required"])
            );

          return (
            min == null ||
            Number(value) >= min ||
            formatMessage(messages["taskCreation.validation.minimum"], { min })
          );
        },
      }}
      render={({ field, fieldState }) => (
        <NumberQuestion
          label={title}
          value={field.value == null ? null : Number(field.value)}
          onChange={(next) => {
            onEdit?.();
            field.onChange(next);
          }}
          min={min}
          badge={
            isSameScalar(field.value, aiValue) ? <AutoFilledBadge /> : undefined
          }
          isRequired={question.required}
          isInvalid={Boolean(fieldState.error)}
          errorMessage={fieldState.error?.message}
        />
      )}
    />
  );
};

/**
 * `picture_upload`, drawn as the hint box plus the uploader.
 *
 * The hints are the only per-answer thing on this screen: painter shows a
 * different line per `surface_type`, so `resolveHints` runs against the flow's
 * saved answers and the resolved keys are formatted here — the component takes
 * plain strings.
 *
 * TODO(RT-4321): hints resolve against saved answers only. No config today has
 * a photo hint conditional on a question from the *same* screen; one that did
 * would need the form's live values watched here instead.
 */
const PictureUploadQuestionField: IQuestionAdapter = ({
  name,
  question,
  categoryKey,
  showLabel,
  onBusyChange,
}) => {
  const { formatMessage } = useI18n();
  const { control } = useFormContext();
  const { answers, draft } = useTaskCreationFlow();
  // The identity's key, not the draft's `signed_id`: in edit mode the cache is
  // keyed on the task id and a `signed_id` would write to an entry nothing reads.
  const { draftKey } = useDraftIdentity();
  const recordUploadedPhoto = useRecordUploadedPhoto(draftKey ?? "");
  const forgetUploadedPhoto = useForgetUploadedPhoto(draftKey ?? "");
  const recordPhotoDescription = useRecordPhotoDescription(draftKey ?? "");
  const title = useQuestionTitle(categoryKey, question.key);

  const hintsTitleMessage = question.hints_intro
    ? getConfigMessage(question.hints_intro)
    : undefined;
  const hints = resolveHints(question, answers).flatMap((key) => {
    const message = getConfigMessage(key);

    // A hint key with no declared message is dropped rather than printed raw —
    // `configMessages.test.ts` is what keeps that from happening quietly.
    return message ? [formatMessage(message)] : [];
  });

  const constraints = question.constraints ?? {};

  return (
    <Controller
      name={name}
      control={control}
      rules={
        question.required
          ? {
              validate: (value) =>
                (Array.isArray(value) && value.length > 0) ||
                formatMessage(messages["taskCreation.validation.required"]),
            }
          : undefined
      }
      render={({ field, fieldState }) => (
        <PictureUploadQuestion
          label={showLabel ? title : undefined}
          hintsTitle={
            hintsTitleMessage ? formatMessage(hintsTitleMessage) : undefined
          }
          hints={hints}
          value={(field.value ?? []) as number[]}
          onChange={field.onChange}
          onDescriptionSaved={recordPhotoDescription}
          existingPhotos={draft?.photo_attachments}
          onPhotoUploaded={recordUploadedPhoto}
          onPhotoRemoved={forgetUploadedPhoto}
          // No `photoAttachableId`: a POST naming a task 401s a visitor, who
          // owns none. Linked to the draft at submit instead.
          category={TASK_PHOTO_CATEGORY}
          maxFiles={constraints.max_files ?? 0}
          allowedExtensions={constraints.allowed_extensions ?? []}
          captionMaxLength={constraints.caption_max_length ?? 0}
          isRequired={question.required}
          isInvalid={Boolean(fieldState.error)}
          errorMessage={fieldState.error?.message}
          onBusyChange={onBusyChange}
        />
      )}
    />
  );
};

/**
 * `text`, drawn as the hint box plus a plain textarea.
 *
 * Same shape as `picture_upload`: the hints are the only per-answer thing on
 * the screen — painter's extra-details lines differ per `surface_type` — so
 * `resolveHints` runs against the flow's saved answers and the resolved keys
 * are formatted here, since the component takes plain strings.
 *
 * TODO(RT-4321): hints resolve against saved answers only, as in the
 * `picture_upload` adapter. A config with a hint conditional on a question from
 * the *same* screen would need the form's live values watched here.
 * TODO(RT-4321): the design shows the description-screen's public-visibility
 * notice under this field. `TextQuestion` has no slot for it and is shared with
 * other screens, so it is not rendered yet.
 */
const TextQuestionField: IQuestionAdapter = ({
  name,
  question,
  categoryKey,
  onEdit,
  showLabel,
}) => {
  const { formatMessage } = useI18n();
  const { control } = useFormContext();
  const { answers } = useTaskCreationFlow();
  const title = useQuestionTitle(categoryKey, question.key);
  const isModerationEnabled = useFeatureFlag(TASK_CREATION_FULL_FLAG);

  const placeholderMessage = question.placeholder
    ? getConfigMessage(question.placeholder)
    : undefined;
  const hintsTitleMessage = question.hints_intro
    ? getConfigMessage(question.hints_intro)
    : undefined;
  const hints = resolveHints(question, answers).flatMap((key) => {
    const message = getConfigMessage(key);

    return message ? [formatMessage(message)] : [];
  });

  return (
    <Controller
      name={name}
      control={control}
      // Unconditional, `required` deciding inside itself: react-hook-form
      // captures `rules` at registration and never drops them again.
      rules={{
        validate: {
          required: (value) =>
            !question.required ||
            Boolean(value) ||
            formatMessage(messages["taskCreation.validation.required"]),
          [CONTACT_INFO_ERROR]: (value) =>
            !isModerationEnabled || noContactInfo(value),
        },
      }}
      render={({ field, fieldState }) => (
        <TextQuestion
          label={showLabel ? title : undefined}
          hintsTitle={
            hintsTitleMessage ? formatMessage(hintsTitleMessage) : undefined
          }
          hints={hints}
          placeholder={
            placeholderMessage ? formatMessage(placeholderMessage) : undefined
          }
          value={(field.value as string) ?? ""}
          onChange={(next) => {
            onEdit?.();
            field.onChange(next);
          }}
          maxLength={question.constraints?.max_length}
          isRequired={question.required}
          // On the message, not the error: a contact-info failure carries none,
          // and `DescriptionField` already renders nothing for it.
          isInvalid={Boolean(fieldState.error?.message)}
          errorMessage={fieldState.error?.message}
        />
      )}
    />
  );
};

/**
 * `date`, drawn as the compact blue pill inside its trigger option's row.
 *
 * Every `date` in the configs today is an inline question (`desired_date` in
 * the "before this date" card), which is why the pill variant is the only one
 * wired up and `showLabel` is not consulted: the option row's own text is the
 * label. A future standalone date question would want `variant="default"`.
 */
const DateQuestionField: IQuestionAdapter = ({
  name,
  question,
  onEdit,
  onActivate,
  inputRef,
}) => {
  const { formatMessage } = useI18n();
  const { control } = useFormContext();

  // `constraints.min` is a sentinel string, not a date — "now" is the only one
  // the configs use. `en-CA` is ISO-shaped *in local time*; `toISOString()`
  // would be UTC and land a day early for users east of UTC in the evening.
  // ponytail: one locale trick beats pulling in a date library for one field.
  const min =
    question.constraints?.min === "now"
      ? new Date().toLocaleDateString("en-CA")
      : undefined;

  // Left blank rather than prefilled to today: a default date would make the
  // required check pass trivially, so picking "Before this date" and hitting
  // Continue without ever touching the pill would silently submit today's date.
  const defaultValue = "";

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      // The rule stays registered whether or not a date is being asked for, and
      // decides inside itself: react-hook-form captures `rules` when the field
      // registers and does not drop them again, so toggling this to `undefined`
      // left the old rule running — which went on failing `handleSubmit` after
      // the user switched to an option that wants no date.
      rules={{
        validate: (value) => {
          if (!value) {
            return (
              !question.required ||
              formatMessage(messages["taskCreation.validation.required"])
            );
          }

          // Only the "now" sentinel implies a lower bound — a date question
          // without it accepts any date, past included.
          if (question.constraints?.min === "now" && !isTodayOrLater(value)) {
            return formatMessage(
              messages["taskCreation.validation.dateInPast"]
            );
          }

          return true;
        },
      }}
      render={({ field }) => (
        <DateQuestion
          ref={inputRef}
          variant="pill"
          value={(field.value as string) ?? ""}
          onChange={(next) => {
            onEdit?.();
            field.onChange(next);
          }}
          onFocus={onActivate}
          onClick={onActivate}
          min={min}
        />
      )}
    />
  );
};

/**
 * `address`, drawn as the pin-prefixed input plus the privacy note under it.
 *
 * The field's own value is the formatted address string. It is *not* an answer:
 * `toDraftFields` moves it to the payload's top-level `location`, and the geo
 * details below travel as their sibling fields — which is why they are written
 * through `setValue` under those exact names rather than nested under this key.
 *
 * `apiKey` is the shared browser key from `@config` — the same one the legacy
 * flow loads the SDK with. `language` follows the UI locale so the suggestions
 * come back in the language the rest of the screen is in.
 *
 * `countryRestriction` is the request's own country (`getCurrentCountry`), which
 * is what the backend geocodes the address against: `UpdateDraft` resolves it
 * inside `current_country` alone (`resolve_place_id` searches `country.towns`),
 * so an address from the other market answers `unresolved_location`. Nothing
 * surfaces that until the final `validate` call, which would fail the flow at
 * publish over a pick made screens earlier — hence the suggestions are held to
 * the one country from the start. Legacy does the same, from `task.country`.
 *
 * The screen only lets the user past an address Google actually resolved to a
 * doorstep — street, number, postal code and city (`missingAddressParts`). The
 * input stays typable, because that is how the suggestions are summoned, but
 * text alone never validates: without a pick there are no coordinates to
 * dispatch a provider to.
 */
const AddressQuestionField: IQuestionAdapter = ({
  name,
  question,
  categoryKey,
  onEdit,
  showLabel,
}) => {
  const { formatMessage, locale } = useI18n();
  const { control, setValue } = useFormContext();
  const { draft } = useTaskCreationFlow();
  const suggestedAddress = useSuggestedAddress();
  const title = useQuestionTitle(categoryKey, question.key);

  // What Google last resolved, and the address accepted without a pick. Both
  // are refs assigned on every render rather than state: `Controller` captures
  // `rules` when it registers the field, so a `validate` closing over a value
  // would keep judging the one from that render. A ref is read fresh.
  const selectedRef = useRef<AddressPlaceDetails | null>(null);
  const trustedRef = useRef<string | undefined>(undefined);
  // Coordinates only ever come from a suggestion, so a draft carrying them
  // holds an address that already passed this check — on the way back from a
  // later screen, or after a reload, it must not have to be picked again. Its
  // components are not persisted, which is why this is a bare string compare
  // rather than another completeness run. The address of the client's last
  // task is trusted only once `useSuggestedAddress` has resolved it complete.
  trustedRef.current =
    draft?.location && draft.latitude != null && draft.longitude != null
      ? draft.location
      : suggestedAddress?.formatted_address;

  const validateAddress = (value: unknown) => {
    const address = String(value ?? "").trim();

    if (!address)
      return (
        !question.required ||
        formatMessage(messages["taskCreation.validation.addressRequired"])
      );

    if (address === trustedRef.current) return true;

    // Two ways to fail, one thing to say: the address was typed and never
    // picked, or it was picked and Google resolved it to less than a doorstep.
    // Both are fixed by the same act — pick a more precise suggestion.
    const selected = selectedRef.current;
    const isPicked =
      selected != null && selected.formattedAddress.trim() === address;

    if (!isPicked || missingAddressParts(selected).length)
      return formatMessage(
        messages["taskCreation.validation.addressIncomplete"]
      );

    return true;
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={{ validate: validateAddress }}
      render={({ field, fieldState }) => (
        <VStack alignItems="stretch" spacing="8px">
          <AddressQuestion
            apiKey={GOOGLE_MAPS_BROWSER_API_KEY}
            language={locale}
            countryRestriction={getCurrentCountry()}
            label={showLabel ? title : undefined}
            value={(field.value as string) ?? ""}
            onChange={(next) => {
              onEdit?.();
              // Typing drops the last pick and its geo siblings: leaving them
              // would pair coordinates with an address the user has moved off.
              // A pick fires this too — Google writes the formatted address
              // through `onChange` first — and `onPlaceSelect` then puts back
              // the details for the address actually chosen.
              selectedRef.current = null;
              setValue("latitude", undefined);
              setValue("longitude", undefined);
              setValue("zip_code", undefined);
              field.onChange(next);
            }}
            onPlaceSelect={(details) => {
              selectedRef.current = details;
              field.onChange(details.formattedAddress);
              setValue("latitude", details.latitude);
              setValue("longitude", details.longitude);
              setValue("zip_code", details.postalCode);
              // TODO(RT-4321): `place_name` is left unset. The only nearby thing
              // the picker gives us is `city` (Google's `locality`), which is
              // not what the backend means by a place name.
            }}
            placeholder={formatMessage(
              getCurrentCountry() === "fr"
                ? messages["taskCreation.address.placeholder.fr"]
                : messages["taskCreation.address.placeholder"]
            )}
            isRequired={question.required}
            isInvalid={Boolean(fieldState.error)}
            errorMessage={fieldState.error?.message}
          />
          <Text fontSize="sm" color="gray.600">
            {formatMessage(messages["taskCreation.address.privacyNote"])}
          </Text>
        </VStack>
      )}
    />
  );
};

export const questionRegistry: Partial<
  Record<IQuestionType, IQuestionAdapter>
> = {
  single_select: SingleSelectQuestionField,
  multi_select: MultiSelectQuestionField,
  integer: NumberQuestionField,
  float: NumberQuestionField,
  picture_upload: PictureUploadQuestionField,
  text: TextQuestionField,
  date: DateQuestionField,
  address: AddressQuestionField,
};

export const getQuestionAdapter = (type: IQuestionType) =>
  questionRegistry[type];
